import React from 'react';
import { Card, Divider, Space, Image } from 'antd';
import { Block, FlexBox, IconRowLeft, Spacer, TextCommon, useLocalesFormat } from '@esg/ui';
import { GetDetailSaleBasicResponse, saleHelper } from '@esg/business-pos';
import { currentLocaleState } from '@esg/ui/state/currentLocale';
import { useAtom } from 'jotai';
import '../Home.scss';
import { translateCodes } from '@/locales';
import { useTranslation } from 'react-i18next';

interface TransactionDetailProps {
    sale: GetDetailSaleBasicResponse;
    handleSetActivePanel?: (key: "LEFT" | "RIGHT") => void;
    isMobile?: boolean;
}

const TransactionDetails = (props: TransactionDetailProps) => {
    const { t } = useTranslation();
    const { formatDate, formatCurrency } = useLocalesFormat();
    const [currentLocale] = useAtom(currentLocaleState);
    const tipAmount = saleHelper.calcTipAmount(props.sale);
    const totalDiscountAmount = saleHelper.calcTotalDiscount(props.sale?.saleDetails ?? []);

    return (
        <Block className='transaction-detail'>
            <Card
                title={
                    <FlexBox direction='column' gap={2}>
                        {props.isMobile && (
                            <>
                                <Spacer height={5}></Spacer>
                                <IconRowLeft color='#101111' onClick={() => props.handleSetActivePanel?.('RIGHT')} />
                            </>
                        )}
                        <Spacer height={15}></Spacer>
                        <TextCommon translateCode={translateCodes.TRANSACTION_DETAILS} />
                        <TextCommon fontSize={14} color='#475467' fontWeight={400}>{formatDate(props.sale?.checkoutAt ?? '', 'h:mm A ddd, ' + currentLocale?.format)}</TextCommon>
                        <Spacer height={10}></Spacer>
                    </FlexBox>
                }
            >
                <Space direction="horizontal" align="start" style={{ width: '100%' }}>
                    <Image alt="Barbershop Logo" width={50} height={50} preview={false} src={props.sale?.branch.logo} ></Image>
                    <FlexBox direction='column' gap={5}>
                        <TextCommon fontWeight={700}>{props.sale?.branch.name}</TextCommon>
                        <TextCommon>{props.sale?.branch.phoneNumber}</TextCommon>
                    </FlexBox>
                </Space>
                <Divider />
                <FlexBox direction='column' gap={8}>
                    <Block>
                        <FlexBox justifyContent='space-between'>
                            <TextCommon>{`${t(translateCodes.CLIENT_NAME)}: `}</TextCommon>
                            <TextCommon>{props.sale?.customer?.fullName} </TextCommon>
                        </FlexBox>
                    </Block>
                    <Block>
                        <FlexBox justifyContent='space-between'>
                            <TextCommon >{`${t(translateCodes.PHONE_NUMBER)}: `}</TextCommon>
                            <TextCommon >{props.sale?.customer?.phone} </TextCommon>
                        </FlexBox>
                    </Block>
                    <Block>
                        <FlexBox justifyContent='space-between'>
                            <TextCommon >{`${t(translateCodes.PAYMENT_TYPE)}: `}</TextCommon>
                            <TextCommon >{props.sale?.paymentMethodSetting?.name}</TextCommon>
                        </FlexBox>
                    </Block>
                    <Block>
                        <FlexBox justifyContent='space-between'>
                            <TextCommon >{`${t(translateCodes.SALE_ID)}: `}</TextCommon>
                            <TextCommon >{props.sale?.code} </TextCommon>
                        </FlexBox>
                    </Block>
                </FlexBox>
                <Divider />
                <FlexBox direction='column' gap={8}>
                    <Block>
                        <FlexBox justifyContent='space-between'>
                            <TextCommon color='#98A2B3' translateCode={translateCodes.DESCRIPTION} />
                            <TextCommon color='#98A2B3' translateCode={translateCodes.PRICE} />
                        </FlexBox>
                    </Block>
                    {props.sale?.saleDetails?.map(o => {
                        const subTotal = (o?.objectPrice ?? 0) * (o?.quantity ?? 0);
                        const showSubTotal = subTotal > (o?.totalAmount ?? 0);

                        return (
                            <>{o.objectType == 'SERVICE' &&
                                <Block>
                                    <TextCommon fontWeight={700}>{`${t(translateCodes.SERVICE)}: `}</TextCommon>
                                    <Block>
                                        <FlexBox justifyContent="space-between">
                                            <Block>
                                                <FlexBox gap={5}>
                                                    <Spacer width={8}></Spacer>
                                                    <TextCommon>•</TextCommon>
                                                    <TextCommon>{o.objectName}</TextCommon>
                                                </FlexBox>
                                            </Block>
                                            <Block>
                                                <FlexBox gap={5}>
                                                    {showSubTotal && <TextCommon textDecoration='line-through' color='#98A2B3'>{formatCurrency(subTotal ?? 0)}</TextCommon>}
                                                    <TextCommon>{formatCurrency(o.totalAmount)}</TextCommon>
                                                </FlexBox>
                                            </Block>
                                        </FlexBox>
                                    </Block>

                                </Block>
                            }
                                {
                                    o.objectType === 'PRODUCT' && <Block>
                                        <TextCommon fontWeight={700}>{`${t(translateCodes.PRICE)}: `}</TextCommon>
                                        <Block>
                                            <FlexBox justifyContent="space-between">
                                                <Block>
                                                    <FlexBox gap={5}>
                                                        <Spacer width={8}></Spacer>
                                                        <TextCommon>•</TextCommon>
                                                        <TextCommon>{o.objectName}</TextCommon>
                                                    </FlexBox>
                                                </Block>
                                                <Block>
                                                    <FlexBox gap={5}>
                                                        {o.subTotal && <TextCommon textDecoration='line-through' color='#98A2B3'>{formatCurrency(o.subTotal ?? 0)}</TextCommon>}
                                                        <TextCommon>{formatCurrency(o.objectPrice)}</TextCommon>
                                                    </FlexBox>
                                                </Block>
                                            </FlexBox>
                                        </Block>
                                    </Block>
                                }
                            </>
                        );
                    })}

                </FlexBox>
                <Divider />
                <FlexBox direction='column' gap={8}>
                    <Block>
                        <FlexBox justifyContent="space-between">
                            <TextCommon>{`${t(translateCodes.SUB_TOTAL)}: `}</TextCommon>
                            <TextCommon>{formatCurrency(props.sale?.subTotalDetail ?? 0)}</TextCommon>
                        </FlexBox>
                    </Block>
                    {
                        totalDiscountAmount
                            ? <Block>
                                <FlexBox justifyContent="space-between">
                                    <TextCommon>{`${t(translateCodes.DISCOUNT)}: `}</TextCommon>
                                    <TextCommon>{formatCurrency(totalDiscountAmount ?? 0)}</TextCommon>
                                </FlexBox>
                            </Block>
                            : null
                    }
                    {
                        props.sale?.taxAmount !== undefined && <Block>
                            <FlexBox justifyContent="space-between">
                                <TextCommon>{`${t(translateCodes.TAX)}: `}</TextCommon>
                                <TextCommon>{formatCurrency(props.sale?.taxAmount ?? 0)}</TextCommon>
                            </FlexBox>
                        </Block>
                    }
                    <Block>
                        <FlexBox justifyContent="space-between">
                            <TextCommon>{`${t(translateCodes.TIP)}: `}</TextCommon>
                            <TextCommon>{formatCurrency(tipAmount)}</TextCommon>
                        </FlexBox>
                    </Block>
                </FlexBox>
                <Divider />
                <FlexBox justifyContent="space-between">
                    <TextCommon fontSize={18} fontWeight={600}>{`${t(translateCodes.TOTAL_AMOUNT).toUpperCase()}: `}</TextCommon>
                    <TextCommon fontSize={18} fontWeight={600}>{formatCurrency(props.sale?.totalAmount ?? 0)}</TextCommon>
                </FlexBox>
            </Card>
        </Block>
    );
};

export default TransactionDetails;
